import { type ReactNode, useState } from 'react'
import { MOCK_DECAY_ITEMS } from '../../_mock-data/decay'
import { useCoachingStore } from '../../store'
import { SEEN_FILTER_LABEL } from '../_lib/seen-filter'
import type { LibraryTab, TypeFilter } from '../curriculum-slice'
import { BrowseTable } from './browse-table'
import { CompanyTags } from './company-tags'
import { DecayTab } from './decay-tab'
import { EloShadeLegend } from './elo-shade-legend'
import {
  appendDefaultFilterRule,
  FilterPanel,
  filterRuleCount,
} from './filter-panel'
import { InterviewTab } from './interview-tab'
import { PresetChips } from './preset-chips'
import { SortPanel } from './sort-panel'
import { SourcesTab } from './sources-tab'
import { TopicTab } from './topic-tab'

const TABS: { id: LibraryTab; label: string }[] = [
  { id: 'browse', label: 'Browse' },
  { id: 'topic', label: '✦ Topic' },
  { id: 'decay', label: `⚠ Decay ${MOCK_DECAY_ITEMS.length}` },
  { id: 'sources', label: 'Sources' },
  { id: 'interview', label: 'Interview' },
]

const TYPE_FILTERS: { id: TypeFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'coding', label: 'Coding' },
  { id: 'sd', label: 'System design' },
  { id: 'behavioral', label: 'Behavioral' },
]

type OpenLibraryPanel = 'sort' | 'filter' | null

const countLabel = (count: number, singular: string, plural: string): string =>
  `${count} ${count === 1 ? singular : plural}`

export function LibraryCard(): ReactNode {
  const { curriculum } = useCoachingStore()
  const [query, setQuery] = useState('')
  const [openPanel, setOpenPanel] = useState<OpenLibraryPanel>(null)
  // Two gates, not one: the filter/sort rules drive the Sources tree too
  // (LG-827), but the seen-state controls and the ELO legend only describe
  // the flat problem tables — an amber "seen" pill over the Sources tree
  // would claim a narrowing that is not happening there.
  const showSeenControls =
    curriculum.libraryTab === 'browse' || curriculum.libraryTab === 'topic'
  const showFilterSortControls =
    showSeenControls || curriculum.libraryTab === 'sources'
  const showBrowseControls = curriculum.libraryTab === 'browse'
  const ruleCount = filterRuleCount(curriculum.filters)
  const sortCount = curriculum.sorts.length

  const togglePanel = (panel: Exclude<OpenLibraryPanel, null>): void => {
    setOpenPanel((current) => (current === panel ? null : panel))
  }

  const addFilter = (): void => {
    curriculum.setFilters(appendDefaultFilterRule(curriculum.filters))
    setOpenPanel('filter')
  }

  const tabBodies: Record<LibraryTab, ReactNode> = {
    browse: <BrowseTable items={curriculum.libraryItems} query={query} />,
    topic: <TopicTab />,
    decay: <DecayTab />,
    sources: <SourcesTab />,
    interview: <InterviewTab />,
  }

  return (
    // At xl: the row splitter (LG-611) bounds this card's height, so it
    // becomes a flex column: the tab bar and the shared table controls keep
    // their natural height and the active tab body takes whatever is left and
    // scrolls inside it. Below xl: there is no divider and every tab body
    // keeps its own fixed cap.
    <section className="min-w-0 rounded-xl border border-(--cd-border) bg-(--cd-shell) p-4 xl:flex xl:h-full xl:min-h-0 xl:flex-col">
      <div className="mb-3 flex flex-wrap items-center gap-2 xl:shrink-0">
        <h2 className="mr-1 text-sm font-semibold text-(--cd-text)">Library</h2>
        <div className="flex flex-wrap gap-1 rounded-full border border-(--cd-border) bg-(--cd-bg) p-0.5">
          {TABS.map((tab) => {
            const active = curriculum.libraryTab === tab.id
            return (
              <button
                key={tab.id}
                type="button"
                aria-pressed={active}
                onClick={() => curriculum.setLibraryTab(tab.id)}
                className="rounded-full px-2.5 py-1 text-[10px] font-bold transition-colors"
                style={{
                  background: active ? 'var(--cd-tab-active)' : 'transparent',
                  color:
                    tab.id === 'decay' && !active
                      ? 'var(--cd-red)'
                      : active
                        ? 'var(--cd-text)'
                        : 'var(--cd-text-muted)',
                }}
              >
                {tab.id === 'decay' && curriculum.persisted
                  ? '⚠ Decay 0'
                  : tab.label}
              </button>
            )
          })}
        </div>
        {/* Tri-state seen filter (LG-362 §8): click cycles unseen-only →
            seen + unseen → seen-only. Amber while it is actually narrowing
            the table, muted in the everything state. Gated by
            showSeenControls (see the two-gate comment above): seen-state
            describes problem rows only, so it stays off the Sources tree. */}
        {showSeenControls ? (
          <button
            type="button"
            onClick={curriculum.cycleSeenFilter}
            className="rounded-full border px-2.5 py-1 text-[10px] font-bold"
            style={{
              borderColor:
                curriculum.seenFilter !== 'all'
                  ? 'color-mix(in oklab, var(--cd-amber) 45%, transparent)'
                  : 'var(--cd-border-2)',
              background:
                curriculum.seenFilter !== 'all'
                  ? 'color-mix(in oklab, var(--cd-amber) 12%, transparent)'
                  : 'transparent',
              color:
                curriculum.seenFilter !== 'all'
                  ? 'var(--cd-amber)'
                  : 'var(--cd-text-muted)',
            }}
          >
            ↻ {SEEN_FILTER_LABEL[curriculum.seenFilter]}
          </button>
        ) : null}
        {/* The overlay query died: the filter above is suspended, so the
            shortfall has to be told (cf. CorpusStatus — unfiltered looks
            exactly like filtered). Warning-pill idiom from schedule-card;
            role="alert" because the node is inserted on failure, and only an
            alert is announced on insertion (corpus-status.tsx's reasoning). */}
        {showSeenControls && curriculum.seenOverlayFailed === true ? (
          <span
            role="alert"
            className="rounded-md border border-dashed border-(--cd-amber)/40 bg-(--cd-amber)/10 px-[7px] py-0.5 text-[10px] font-bold text-(--cd-amber)"
          >
            ⚠ Seen history could not be loaded — showing every row
          </span>
        ) : null}
        {showSeenControls ? <EloShadeLegend /> : null}
      </div>

      {showFilterSortControls ? (
        <div className="space-y-2 xl:shrink-0">
          <div className="flex flex-wrap items-center gap-2">
            {showBrowseControls ? (
              <>
                <input
                  type="search"
                  aria-label="Search library"
                  placeholder="Search…"
                  value={query}
                  onChange={(event) => setQuery(event.currentTarget.value)}
                  className="w-44 rounded-lg border border-(--cd-border) bg-(--cd-bg) px-2.5 py-1.5 text-xs text-(--cd-text) outline-none focus:border-(--cd-text-faint)"
                />
                <div className="flex flex-wrap gap-1">
                  {TYPE_FILTERS.map((filter) => {
                    const active = curriculum.typeFilter === filter.id
                    return (
                      <button
                        key={filter.id}
                        type="button"
                        aria-pressed={active}
                        onClick={() => curriculum.setTypeFilter(filter.id)}
                        className="rounded-full border px-2.5 py-1 text-[10px] font-semibold"
                        style={{
                          borderColor: active
                            ? 'var(--cd-text-muted)'
                            : 'var(--cd-border-2)',
                          background: active
                            ? 'color-mix(in oklab, var(--cd-text) 8%, transparent)'
                            : 'transparent',
                          color: active
                            ? 'var(--cd-text)'
                            : 'var(--cd-text-muted)',
                        }}
                      >
                        {filter.label}
                      </button>
                    )
                  })}
                </div>
              </>
            ) : null}
            <div className="ml-auto flex items-center gap-1.5">
              <button
                type="button"
                aria-expanded={openPanel === 'sort'}
                aria-controls="library-sort-panel"
                onClick={() => togglePanel('sort')}
                className="relative inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-(--cd-border-2) px-3 py-1 text-[11px] font-bold hover:text-(--cd-text)"
                style={{
                  background:
                    openPanel === 'sort'
                      ? 'var(--cd-tab-active)'
                      : 'transparent',
                  color:
                    openPanel === 'sort'
                      ? 'var(--cd-text)'
                      : 'var(--cd-text-muted)',
                }}
              >
                ⇅ {countLabel(sortCount, 'sort', 'sorts')} ▾
                {sortCount > 0 ? (
                  <span
                    aria-hidden="true"
                    className="absolute -top-0.5 -right-0.5 size-1.5 rounded-full bg-(--cd-amber)"
                  />
                ) : null}
              </button>
              <button
                type="button"
                aria-expanded={openPanel === 'filter'}
                aria-controls="library-filter-panel"
                onClick={() => togglePanel('filter')}
                className="relative inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-(--cd-border-2) px-3 py-1 text-[11px] font-bold hover:text-(--cd-text)"
                style={{
                  background:
                    openPanel === 'filter'
                      ? 'var(--cd-tab-active)'
                      : 'transparent',
                  color:
                    openPanel === 'filter'
                      ? 'var(--cd-text)'
                      : 'var(--cd-text-muted)',
                }}
              >
                ≡ {countLabel(ruleCount, 'rule', 'rules')} ▾
                {ruleCount > 0 ? (
                  <span
                    aria-hidden="true"
                    className="absolute -top-0.5 -right-0.5 size-1.5 rounded-full bg-(--cd-amber)"
                  />
                ) : null}
              </button>
              <button
                type="button"
                onClick={addFilter}
                className="whitespace-nowrap text-[10.5px] font-semibold text-(--cd-text-muted) hover:text-(--cd-text)"
              >
                + Filter
              </button>
            </div>
          </div>

          {openPanel === 'sort' ? <SortPanel /> : null}
          {openPanel === 'filter' ? <FilterPanel /> : null}
          {showBrowseControls && !curriculum.persisted ? <PresetChips /> : null}
          {showSeenControls && !curriculum.persisted ? <CompanyTags /> : null}
        </div>
      ) : null}

      <div className="mt-3 xl:flex xl:min-h-0 xl:flex-1 xl:flex-col">
        {tabBodies[curriculum.libraryTab]}
      </div>
    </section>
  )
}
